import { TranslocoPipe } from '@jsverse/transloco';
import { Component } from '@angular/core';
import { TableModule } from '@openng/optimus-ui/table';
import { AppCardComponent } from '../../../design/components/app-card/app-card.component';
import { AppTableShellComponent } from '../../../design/components/app-table-shell/app-table-shell.component';
import { MvpBadgeComponent } from '../../../design/components/mvp-badge/mvp-badge.component';
import { PlayerAvatarComponent } from '../../../design/components/player-avatar/player-avatar.component';
import { RevealDirective } from '../../../shared/animations/reveal.directive';
import { MatchPointOverviewComponent } from '../../matches/components/match-point-overview.component';
import { DEMO_MATCH, DEMO_PLAYER_METRICS } from '../domain/landing-demo-data';

// Match-detail showcase: the real point-overview component and the same shared
// table treatment used by Match Review, both backed by staged local data.
@Component({
  selector: 'app-landing-match-showcase',
  standalone: true,
  imports: [TranslocoPipe, TableModule, RevealDirective, AppCardComponent, AppTableShellComponent, MvpBadgeComponent, PlayerAvatarComponent, MatchPointOverviewComponent],
  template: `
    <section class="match-showcase" aria-labelledby="match-showcase-title">
      <div class="section-heading" appReveal>
        <span>{{ 'landing.matchDetail' | transloco }}</span>
        <h2 id="match-showcase-title">{{ 'landing.everyPointReplayedFromTheEventLog' | transloco }}</h2>
        <p>{{ 'landing.replayEveryRallySeeHowItWasWonAnd' | transloco }}</p>
      </div>

      <app-match-point-overview appReveal [appRevealIndex]="1" [match]="demoMatch" />

      <app-card appReveal [appRevealIndex]="2" [title]="'landing.playerMetrics' | transloco" [eyebrow]="'landing.sortedByPoints' | transloco">
        <app-table-shell [ariaLabel]="'landing.playerMetrics' | transloco">
          <p-table [value]="playerMetrics" [sortField]="'points'" [sortOrder]="-1" styleClass="app-table" [tableStyle]="{ 'min-width': '50rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th class="app-table__identity" pSortableColumn="name">{{ 'landing.player' | transloco }} <p-sortIcon field="name" /></th>
                <th class="app-table__number" pSortableColumn="points">{{ 'landing.points' | transloco }} <p-sortIcon field="points" /></th>
                <th class="app-table__number" pSortableColumn="serves">{{ 'landing.serves' | transloco }} <p-sortIcon field="serves" /></th>
                <th class="app-table__number" pSortableColumn="receives">{{ 'landing.receives' | transloco }} <p-sortIcon field="receives" /></th>
                <th class="app-table__number" pSortableColumn="break">{{ 'landing.break' | transloco }} <p-sortIcon field="break" /></th>
                <th class="app-table__number" pSortableColumn="sideout">{{ 'landing.sideOut' | transloco }} <p-sortIcon field="sideout" /></th>
                <th class="app-table__number" pSortableColumn="ace">{{ 'landing.ace' | transloco }} <p-sortIcon field="ace" /></th>
                <th class="app-table__number" pSortableColumn="pressure">{{ 'landing.pressure' | transloco }} <p-sortIcon field="pressure" /></th>
                <th class="app-table__number" pSortableColumn="diff">+/- <p-sortIcon field="diff" /></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                  <td class="app-table__identity">
                    <div class="app-table__identity-content">
                      <app-player-avatar [name]="row.name" />
                      <span class="app-table__identity-copy">
                        <strong class="app-table__identity-title">{{ row.name }}</strong>
                        @if (row.isMvp) { <app-mvp-badge>{{ 'landing.mvp' | transloco }}</app-mvp-badge> }
                      </span>
                    </div>
                  </td>
                  <td class="app-table__number"><span class="app-table__value app-table__value--strong">{{ row.points }}</span></td>
                  <td class="app-table__number"><span class="app-table__value">{{ row.serves }}</span></td>
                  <td class="app-table__number"><span class="app-table__value">{{ row.receives }}</span></td>
                  <td class="app-table__number"><span class="app-table__value">{{ row.break }}<small>%</small></span></td>
                  <td class="app-table__number"><span class="app-table__value">{{ row.sideout }}<small>%</small></span></td>
                  <td class="app-table__number"><span class="app-table__value">{{ row.ace }}<small>%</small></span></td>
                  <td class="app-table__number"><span class="app-table__value">{{ row.pressure }}<small>%</small></span></td>
                  <td class="app-table__number"><span class="app-table__value" [class.app-table__value--positive]="row.diff > 0" [class.app-table__value--negative]="row.diff < 0">{{ row.diff > 0 ? '+' : '' }}{{ row.diff }}</span></td>
                </tr>
            </ng-template>
          </p-table>
        </app-table-shell>
      </app-card>
    </section>
  `,
  styles: [`
    .match-showcase {
      display: grid;
      gap: var(--space-6);
      margin-inline: auto;
      width: min(100%, 74rem);
    }

    .section-heading {
      display: grid;
      gap: var(--space-2);
      max-width: 44rem;
    }

    .section-heading span {
      color: var(--color-primary);
      font-size: var(--font-size-label);
      font-weight: 900;
      text-transform: uppercase;
    }

    h2, p { margin: 0; }

    h2 {
      font-size: clamp(1.75rem, 4vw, 3.2rem);
      letter-spacing: 0;
      line-height: 1.05;
    }

    .section-heading p {
      color: var(--color-text-secondary);
      line-height: 1.7;
    }

  `],
})
export class LandingMatchShowcaseComponent {
  readonly demoMatch = DEMO_MATCH;
  readonly playerMetrics = DEMO_PLAYER_METRICS;
}
